// Predictive Alerts API Service
// This service will be used to fetch alerts from the backend when implemented

import { api } from './api';

// Fetch all predictive alerts
export const getPredictiveAlerts = async (filters = {}) => {
  try {
    const response = await api.get('/api/predictive-alerts', {
      params: {
        severity: filters.severity || 'ALL',
        mineral: filters.mineral || 'ALL',
        status: filters.status || 'ALL',
        limit: filters.limit || 50
      }
    });
    
    if (response.data && response.data.success) {
      return response.data.alerts;
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error) {
    console.error('Error fetching predictive alerts:', error);
    // Return empty array instead of mock data
    return [];
  }
};

// Acknowledge an alert
export const acknowledgeAlert = async (alertId, acknowledgedBy = 'user') => {
  try {
    const response = await api.post(`/api/predictive-alerts/${alertId}/acknowledge`, null, {
      params: { acknowledged_by: acknowledgedBy }
    });
    
    if (response.data && response.data.success) {
      return response.data;
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error) {
    console.error('Error acknowledging alert:', error);
    throw error;
  }
};

// Get alert statistics
export const getAlertStats = async () => {
  try {
    const response = await api.get('/api/predictive-alerts/stats');
    
    if (response.data && response.data.success) {
      return response.data.stats;
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error) {
    console.error('Error fetching alert stats:', error);
    // Return empty stats instead of mock data
    return {
      total: 0,
      critical: 0,
      elevated: 0,
      watchlist: 0
    };
  }
};

// Get list of unique minerals
export const getMineralsList = async () => {
  try {
    const response = await api.get('/api/predictive-alerts/minerals');
    
    if (response.data && response.data.success) {
      return response.data.minerals;
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error) {
    console.error('Error fetching minerals list:', error);
    // Return empty array instead of mock data
    return [];
  }
};

// Search alerts
export const searchAlerts = async (searchTerm, limit = 20) => {
  try {
    const response = await api.get('/api/predictive-alerts/search', {
      params: {
        q: searchTerm,
        limit: limit
      }
    });
    
    if (response.data && response.data.success) {
      return response.data.results;
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error) {
    console.error('Error searching alerts:', error);
    return [];
  }
};

// Generate a new prediction using Claude
export const generatePrediction = async (mineral, predictionType = null) => {
  try {
    const params = { mineral };
    if (predictionType) {
      params.prediction_type = predictionType;
    }
    
    const response = await api.post('/api/predictive-alerts/generate', null, { params });
    
    if (response.data && response.data.success) {
      return response.data;
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error) {
    console.error('Error generating prediction:', error);
    throw error;
  }
};

// Generate batch predictions
export const generateBatchPredictions = async (minerals, predictionTypes = null) => {
  try {
    const params = { minerals };
    if (predictionTypes && predictionTypes.length > 0) {
      params.prediction_types = predictionTypes;
    }
    
    const response = await api.post('/api/predictive-alerts/generate-batch', null, { params });
    
    if (response.data && response.data.success) {
      return response.data;
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error) {
    console.error('Error generating batch predictions:', error);
    throw error;
  }
};

// Get available minerals for prediction
export const getAvailableMinerals = async () => {
  try {
    const response = await api.get('/api/predictive-alerts/available-minerals');
    
    if (response.data && response.data.success) {
      return response.data.minerals;
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error) {
    console.error('Error fetching available minerals:', error);
    return [];
  }
};

// Check Claude service status
export const getClaudeStatus = async () => {
  try {
    const response = await api.get('/api/predictive-alerts/claude-status');
    // Return the data directly - the API already returns the correct structure
    if (response.data) {
      return response.data;
    }
    return {
      success: false,
      available: false,
      message: 'Invalid response format'
    };
  } catch (error) {
    console.error('Error checking Claude status:', error);
    // Return a structure that allows the button to work but shows a warning
    return {
      success: false,
      available: false,
      connection_test: false,
      message: error.response?.data?.detail || 'Error checking Claude status. The service may still work.'
    };
  }
};

